/**
 * Predictive page prefetching.
 *
 * Strategy:
 * 1. Speculation Rules API (Chrome/Edge) — prerender on moderate eagerness
 *    when visitor hovers a link; native, very fast.
 * 2. Fallback for other browsers: <link rel="prefetch"> on hover/touchstart
 *    plus viewport-based prefetching during idle time (Quicklink-style).
 *
 * Both skip cart, checkout, account, search and external URLs to avoid
 * stale data and wasted bandwidth.
 */
(function () {
  'use strict';

  if (window.__prefetchPagesLoaded) return;
  window.__prefetchPagesLoaded = true;

  var SKIP_PATHS = ['/cart', '/checkout', '/account', '/search', '/admin'];
  var SKIP_FILE_EXT = /\.(pdf|zip|mp4|webm|mov|jpg|jpeg|png|gif|webp|svg)$/i;
  var prefetched = new Set();

  function isPrefetchable(a) {
    if (!a || a.tagName !== 'A' || !a.href) return false;
    if (a.target && a.target !== '' && a.target !== '_self') return false;
    if (a.hasAttribute('data-no-prefetch') || a.closest('[data-no-prefetch]')) return false;

    var url;
    try { url = new URL(a.href); } catch (e) { return false; }

    if (url.origin !== location.origin) return false;
    if (url.pathname === location.pathname && url.search === location.search) return false;
    if (SKIP_FILE_EXT.test(url.pathname)) return false;

    for (var i = 0; i < SKIP_PATHS.length; i++) {
      if (url.pathname.startsWith(SKIP_PATHS[i])) return false;
    }
    return true;
  }

  function prefetch(href) {
    if (prefetched.has(href)) return;
    prefetched.add(href);
    var link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = href;
    link.as = 'document';
    document.head.appendChild(link);
  }

  // 1) Native Speculation Rules (only when supported)
  if (HTMLScriptElement.supports && HTMLScriptElement.supports('speculationrules')) {
    var rules = {
      prerender: [{
        where: {
          and: [
            { href_matches: '/*' },
            { not: { href_matches: '/cart*' } },
            { not: { href_matches: '/checkout*' } },
            { not: { href_matches: '/account*' } },
            { not: { href_matches: '/admin*' } },
            { not: { href_matches: '/search*' } }
          ]
        },
        eagerness: 'moderate'
      }],
      prefetch: [{
        where: {
          and: [
            { href_matches: '/*' },
            { not: { href_matches: '/cart*' } },
            { not: { href_matches: '/checkout*' } },
            { not: { href_matches: '/account*' } }
          ]
        },
        eagerness: 'conservative'
      }]
    };
    var script = document.createElement('script');
    script.type = 'speculationrules';
    script.textContent = JSON.stringify(rules);
    document.head.appendChild(script);
  }

  // 2) Hover / touch prefetch (universal fallback)
  var hoverTimer;
  function onHover(e) {
    var a = e.target.closest && e.target.closest('a[href]');
    if (!a || !isPrefetchable(a)) return;
    clearTimeout(hoverTimer);
    hoverTimer = setTimeout(function () { prefetch(a.href); }, 65);
  }
  function onLeave() {
    clearTimeout(hoverTimer);
  }
  function onTouch(e) {
    var a = e.target.closest && e.target.closest('a[href]');
    if (a && isPrefetchable(a)) prefetch(a.href);
  }

  document.addEventListener('mouseover', onHover, { passive: true, capture: true });
  document.addEventListener('mouseout', onLeave, { passive: true, capture: true });
  document.addEventListener('touchstart', onTouch, { passive: true, capture: true });
  document.addEventListener('focusin', onHover, { passive: true, capture: true });

  // 3) Viewport-based idle prefetch (Quicklink-style)
  if ('IntersectionObserver' in window) {
    var idle = window.requestIdleCallback || function (cb) { return setTimeout(cb, 200); };
    var conn = navigator.connection;
    var slow = conn && (conn.saveData || /2g/.test(conn.effectiveType || ''));

    if (!slow) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            var a = entry.target;
            io.unobserve(a);
            if (isPrefetchable(a)) {
              idle(function () { prefetch(a.href); });
            }
          }
        });
      }, { rootMargin: '300px 0px' });

      function observeAll() {
        document.querySelectorAll('a[href]').forEach(function (a) { io.observe(a); });
      }

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', observeAll, { once: true });
      } else {
        observeAll();
      }

      // Re-observe links that get added dynamically (e.g. cart sidebar updates)
      var mo = new MutationObserver(function (mutations) {
        mutations.forEach(function (m) {
          m.addedNodes && m.addedNodes.forEach(function (node) {
            if (node.nodeType !== 1) return;
            if (node.tagName === 'A' && node.href) io.observe(node);
            else if (node.querySelectorAll) node.querySelectorAll('a[href]').forEach(function (a) { io.observe(a); });
          });
        });
      });
      mo.observe(document.body, { childList: true, subtree: true });
    }
  }
})();
